import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import Modal from './Common/Modal';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY } from '../config';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const ADMIN_ROLES = ['receptionist', 'lab_attendant', 'accountant', 'librarian', 'principal', 'superintendent'];

const PERSON_TYPES = [
  { value: 'teacher', label: '👨‍🏫 Teacher' },
  { value: 'admin', label: '🧑‍💼 Admin Staff (Accountant / Librarian / Receptionist / Lab Attendant)' },
  { value: 'staff', label: '🧹 Support Staff (Peon / Guard / Driver)' },
];

export default function SalaryManagement() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canWrite = ['admin', 'superintendent', 'accountant'].includes(user?.role);

  const [salaries, setSalaries] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [adminStaff, setAdminStaff] = useState([]);
  const [staff, setStaff] = useState([]);
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);

  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ amount: 0, notes: '', adjustments: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    status: 'all',
    person_type: 'all',
    campus_id: 'all',
  });

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const [form, setForm] = useState({
    person_type: 'teacher',
    person_id: '',
    amount: 0,
    month: currentMonth,
    year: currentYear,
    notes: '',
  });

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters.month, filters.year, filters.status, filters.person_type, filters.campus_id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salRes, usersRes, staffRes, classRes, campusRes] = await Promise.all([
        api.get('/salaries/'),
        api.get('/users/'),
        api.get('/staff/'),
        api.get('/classes/').catch(() => ({ data: [] })),
        api.get('/campuses/').catch(() => ({ data: [] })),
      ]);
      setSalaries(salRes.data);

      const allUsers = usersRes.data || [];
      setTeachers(allUsers.filter(u => u.role === 'teacher'));
      setAdminStaff(allUsers.filter(u => ADMIN_ROLES.includes(u.role)));

      setStaff(staffRes.data || []);
      setClasses(classRes.data || []);
      setCampuses(campusRes.data || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Resolve campus_id for a salary record (all 3 person types) ───
  const resolveCampusId = (salary) => {
    const pt = salary.person_type;

    if (pt === 'teacher') {
      const cls = classes.find(c => c.teacher_id === salary.person_id);
      if (cls) return cls.campus_id;
      const t = teachers.find(x => x.id === salary.person_id);
      return t?.campus_id || null;
    }

    if (pt === 'admin') {
      const a = adminStaff.find(x => x.id === salary.person_id);
      return a?.campus_id || null;
    }

    const s = staff.find(x => x.id === salary.person_id);
    return s?.campus_id || null;
  };

  // ─── Identity resolver for the Details column ───
  const resolveIdentity = (salary) => {
    const pt = salary.person_type;

    if (pt === 'teacher') {
      const cls = classes.find(c => c.teacher_id === salary.person_id);
      if (cls) {
        const campus = campuses.find(c => c.id === cls.campus_id);
        return {
          primary: `Class ${cls.name}${cls.section ? ` · ${cls.section}` : ''}`,
          secondary: campus?.name || '',
        };
      }
      return { primary: 'No class assigned', secondary: '' };
    }

    if (pt === 'admin') {
      const admin = adminStaff.find(a => a.id === salary.person_id);
      const campus = admin ? campuses.find(c => c.id === admin.campus_id) : null;
      return {
        primary: admin?.role ? admin.role.charAt(0).toUpperCase() + admin.role.slice(1) : 'Admin Staff',
        secondary: campus?.name || 'Not assigned',
      };
    }

    const s = staff.find(x => x.id === salary.person_id);
    const campus = s ? campuses.find(c => c.id === s.campus_id) : null;
    return {
      primary: s?.designation || 'Support Staff',
      secondary: campus?.name || '',
    };
  };

  // ═══════════════════════════════════════════════
  // Actions
  // ═══════════════════════════════════════════════
  const handleGenerateMonthly = async () => {
    if (!window.confirm(`Generate salary records for ${MONTHS[filters.month - 1]}/${filters.year}?\n\nThis includes Teachers + Admin Staff + Support Staff.`)) return;
    try {
      const res = await api.post(`/salaries/generate-monthly?month=${filters.month}&year=${filters.year}`);
      const b = res.data.breakdown || {};
      alert(
        `${res.data.message}\n\n` +
        `Teachers: ${b.teachers || 0}\n` +
        `Admin Staff: ${b.admin || 0}\n` +
        `Support Staff: ${b.staff || 0}`
      );
      fetchData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddSalary = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    try {
      let personName = '';
      const id = parseInt(form.person_id);

      if (form.person_type === 'teacher') {
        const t = teachers.find(x => x.id === id);
        if (t) personName = `${t.first_name} ${t.last_name}`;
      } else if (form.person_type === 'admin') {
        const a = adminStaff.find(x => x.id === id);
        if (a) personName = `${a.first_name} ${a.last_name}`;
      } else {
        const s = staff.find(x => x.id === id);
        if (s) personName = `${s.first_name} ${s.last_name}`;
      }

      if (!personName) { alert('Please select a person'); return; }

      await api.post('/salaries/', {
        ...form,
        person_id: id,
        person_name: personName,
        amount: parseFloat(form.amount) || 0,
        adjustments: [],
      });
      setModalOpen(false);
      fetchData();
      resetForm();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleMarkPaid = async (salaryId) => {
    try {
      await api.put(`/salaries/${salaryId}`, {
        status: 'Paid',
        paid_date: new Date().toISOString().split('T')[0],
        payment_method: 'Cash',
      });
      setPayModal(null);
      fetchData();
      setTimeout(() => {
        if (window.confirm('Salary marked as paid. Download the payslip now?')) {
          downloadPayslip(salaryId);
        }
      }, 200);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleBulkMarkPaid = async () => {
    const selected = data.filter(row => selectedIds.has(row._salaryId));
    const pending = selected.filter(row => row._status !== 'Paid');
    if (pending.length === 0) { alert('No pending salaries selected'); return; }

    const totalNet = pending.reduce((s, r) => s + r._net, 0);
    if (!window.confirm(`Mark ${pending.length} salary record(s) as Paid?\n\nTotal: ${CURRENCY}${totalNet.toLocaleString()}`)) return;

    let ok = 0, fail = 0;
    const today = new Date().toISOString().split('T')[0];
    for (const row of pending) {
      try {
        await api.put(`/salaries/${row._salaryId}`, {
          status: 'Paid', paid_date: today, payment_method: 'Bulk',
        });
        ok++;
      } catch { fail++; }
    }
    alert(`✅ Marked ${ok} paid${fail > 0 ? ` · ❌ ${fail} failed` : ''}`);
    setSelectedIds(new Set());
    fetchData();
  };

  const downloadPayslip = (salaryId) => {
    const token = localStorage.getItem('token');
    fetch(`/api/salaries/${salaryId}/payslip`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => { if (!res.ok) throw new Error('Failed to fetch payslip'); return res.text(); })
      .then(html => {
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const win = window.open(url, '_blank');
        if (!win) alert('Please allow popups to view the payslip');
      })
      .catch(err => alert('Error: ' + err.message));
  };

  const openEditModal = (salary) => {
    setEditModal(salary);
    setEditForm({
      amount: salary.amount,
      notes: salary.notes || '',
      adjustments: salary.adjustments ? [...salary.adjustments] : [],
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/salaries/${editModal.id}`, {
        amount: parseFloat(editForm.amount),
        adjustments: editForm.adjustments,
        notes: editForm.notes || null,
      });
      setEditModal(null);
      fetchData();
      alert('✅ Salary updated');
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const addAdjustment = (type) => {
    setEditForm({ ...editForm, adjustments: [...editForm.adjustments, { type, reason: '', value: 0 }] });
  };
  const updateAdjustment = (idx, field, value) => {
    const next = [...editForm.adjustments];
    next[idx][field] = field === 'value' ? parseFloat(value) || 0 : value;
    setEditForm({ ...editForm, adjustments: next });
  };
  const removeAdjustment = (idx) => {
    setEditForm({ ...editForm, adjustments: editForm.adjustments.filter((_, i) => i !== idx) });
  };

  const resetForm = () => {
    setForm({
      person_type: 'teacher', person_id: '', amount: 0,
      month: currentMonth, year: currentYear, notes: '',
    });
  };

  // ─── Apply filters ───
  const filteredSalaries = salaries.filter(s => {
    if (filters.month && s.month !== parseInt(filters.month)) return false;
    if (filters.year && s.year !== parseInt(filters.year)) return false;
    if (filters.status !== 'all' && s.status !== filters.status) return false;
    if (filters.person_type !== 'all' && s.person_type !== filters.person_type) return false;
    if (filters.campus_id !== 'all') {
      const salaryCampusId = resolveCampusId(s);
      if (salaryCampusId !== parseInt(filters.campus_id)) return false;
    }
    return true;
  });

  const computeNet = (s) => {
    const basic = s.amount || 0;
    const adj = s.adjustments || [];
    const adds = adj.filter(a => a.type === 'add').reduce((sum, a) => sum + (a.value || 0), 0);
    const ded = adj.filter(a => a.type === 'deduct').reduce((sum, a) => sum + (a.value || 0), 0);
    return basic + adds - ded;
  };

  const totals = {
    all: filteredSalaries.reduce((sum, s) => sum + computeNet(s), 0),
    paid: filteredSalaries.filter(s => s.status === 'Paid').reduce((sum, s) => sum + computeNet(s), 0),
    pending: filteredSalaries.filter(s => s.status === 'Pending').reduce((sum, s) => sum + computeNet(s), 0),
  };

  // ─── Per-campus breakdown ───
  const campusBreakdown = campuses.map(c => {
    const inCampus = filteredSalaries.filter(s => resolveCampusId(s) === c.id);
    const total = inCampus.reduce((sum, s) => sum + computeNet(s), 0);
    const count = inCampus.length;
    return { campus: c, total, count };
  }).filter(x => x.count > 0);

  const typeBadge = (pt) => {
    if (pt === 'teacher') return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">🎓 Teacher</span>;
    if (pt === 'admin') return <span className="px-2 py-1 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">🧑‍💼 Admin Staff</span>;
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-800">🧹 Support Staff</span>;
  };

  // Build columns — checkbox column only if canWrite
  const columns = [
    ...(canWrite ? [''] : []),
    'Name', 'Details', 'Type', 'Basic', 'Adjustments',
    'Net', 'Month/Year', 'Status', 'Actions',
  ];

  const data = filteredSalaries.map(s => {
    const basic = s.amount || 0;
    const adj = s.adjustments || [];
    const adds = adj.filter(a => a.type === 'add').reduce((sum, a) => sum + (a.value || 0), 0);
    const ded = adj.filter(a => a.type === 'deduct').reduce((sum, a) => sum + (a.value || 0), 0);
    const net = basic + adds - ded;
    const identity = resolveIdentity(s);

    const row = {
      _salaryId: s.id,
      _status: s.status,
      _net: net,
      Name: s.person_name,
      Details: (
        <div className="text-xs leading-tight">
          <div className="font-medium text-gray-800">{identity.primary}</div>
          {identity.secondary && (
            <div className="text-gray-500 text-[10px] mt-0.5">
              <i className="fas fa-map-marker-alt mr-1"></i>
              {identity.secondary}
            </div>
          )}
        </div>
      ),
      Type: typeBadge(s.person_type),
      Basic: `${CURRENCY}${basic.toLocaleString()}`,
      Adjustments: (
        <div className="text-xs">
          {adds > 0 && <div className="text-green-600 font-semibold">+{CURRENCY}{adds.toLocaleString()}</div>}
          {ded > 0 && <div className="text-red-600 font-semibold">−{CURRENCY}{ded.toLocaleString()}</div>}
          {adds === 0 && ded === 0 && <span className="text-gray-400">—</span>}
        </div>
      ),
      Net: <span className="font-bold">{CURRENCY}{net.toLocaleString()}</span>,
      'Month/Year': `${s.month}/${s.year}`,
      Status: (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          s.status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}>{s.status}</span>
      ),
      Actions: (
        <div className="flex gap-1 flex-wrap">
          {canWrite && (
            <button
              onClick={() => openEditModal(s)}
              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
            >
              <i className="fas fa-edit"></i> Edit
            </button>
          )}
          {s.status === 'Pending' ? (
            canWrite && (
              <button
                onClick={() => setPayModal(s)}
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                <i className="fas fa-check"></i> Mark Paid
              </button>
            )
          ) : (
            <button
              onClick={() => downloadPayslip(s.id)}
              className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded hover:bg-purple-200"
            >
              <i className="fas fa-file-invoice"></i> Payslip
            </button>
          )}
        </div>
      ),
    };

    // Attach the checkbox cell only if canWrite
    if (canWrite) {
      row[''] = (
        <input
          type="checkbox"
          className="w-4 h-4 cursor-pointer"
          checked={selectedIds.has(s.id)}
          disabled={s.status === 'Paid'}
          onChange={(e) => {
            const next = new Set(selectedIds);
            if (e.target.checked) next.add(s.id);
            else next.delete(s.id);
            setSelectedIds(next);
          }}
        />
      );
    }

    return row;
  });

  const selectableIds = data.filter(r => r._status !== 'Paid').map(r => r._salaryId);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

  const personOptions =
    form.person_type === 'teacher' ? teachers :
    form.person_type === 'admin' ? adminStaff :
    staff;

  const editNet = (() => {
    const basic = parseFloat(editForm.amount) || 0;
    const adds = editForm.adjustments.filter(a => a.type === 'add').reduce((s, a) => s + (a.value || 0), 0);
    const ded = editForm.adjustments.filter(a => a.type === 'deduct').reduce((s, a) => s + (a.value || 0), 0);
    return basic + adds - ded;
  })();

  const selectedCount = data.filter(r => selectedIds.has(r._salaryId)).length;
  const selectedTotal = data.filter(r => selectedIds.has(r._salaryId)).reduce((s, r) => s + r._net, 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">💰 Salary Management</h2>
        {canWrite && (
          <div className="flex gap-2">
            <button
              onClick={handleGenerateMonthly}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 text-sm"
            >
              <i className="fas fa-magic mr-1"></i> Auto-Generate Monthly
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
            >
              <i className="fas fa-plus mr-1"></i> Add Salary
            </button>
          </div>
        )}
      </div>

      {/* ─── Read-only banner for principal / superintendent ─── */}
      {!canWrite && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3 rounded mb-4 flex items-center gap-2">
          <i className="fas fa-eye"></i>
          <span>You can view salaries and download payslips. Only admin and accountant can create or modify records.</span>
        </div>
      )}

      {/* ─── Financial summary ─── */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-white p-4 rounded shadow">
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold">{CURRENCY}{totals.all.toLocaleString()}</p>
        </div>
        <div className="bg-green-50 p-4 rounded shadow">
          <p className="text-sm text-green-600">Paid</p>
          <p className="text-2xl font-bold text-green-700">{CURRENCY}{totals.paid.toLocaleString()}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded shadow">
          <p className="text-sm text-yellow-600">Pending</p>
          <p className="text-2xl font-bold text-yellow-700">{CURRENCY}{totals.pending.toLocaleString()}</p>
        </div>
      </div>

      {/* ─── Per-campus breakdown (only when All Campuses) ─── */}
      {filters.campus_id === 'all' && campusBreakdown.length > 1 && (
        <div className="bg-white p-3 rounded shadow mb-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">
            <i className="fas fa-building mr-1"></i> Breakdown by Campus
          </p>
          <div className="flex flex-wrap gap-3">
            {campusBreakdown.map(cb => (
              <button
                key={cb.campus.id}
                onClick={() => setFilters({ ...filters, campus_id: String(cb.campus.id) })}
                className="flex-1 min-w-[180px] p-3 rounded border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition text-left"
              >
                <p className="text-xs text-gray-500">{cb.campus.name}</p>
                <p className="text-lg font-bold text-blue-700">{CURRENCY}{cb.total.toLocaleString()}</p>
                <p className="text-[10px] text-gray-400">{cb.count} record{cb.count !== 1 ? 's' : ''}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Filters ─── */}
      <div className="bg-gray-50 p-3 rounded mb-4 flex flex-wrap gap-3 items-center">
        <select
          className="p-2 border rounded text-sm"
          value={filters.month}
          onChange={(e) => setFilters({ ...filters, month: e.target.value })}
        >
          {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select
          className="p-2 border rounded text-sm"
          value={filters.year}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
        >
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          className="p-2 border rounded text-sm"
          value={filters.campus_id}
          onChange={(e) => setFilters({ ...filters, campus_id: e.target.value })}
        >
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          className="p-2 border rounded text-sm"
          value={filters.person_type}
          onChange={(e) => setFilters({ ...filters, person_type: e.target.value })}
        >
          <option value="all">All Types</option>
          <option value="teacher">🎓 Teachers</option>
          <option value="admin">🧑‍💼 Admin Staff</option>
          <option value="staff">🧹 Support Staff</option>
        </select>
        <select
          className="p-2 border rounded text-sm"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="all">All Status</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
        </select>

        {(filters.campus_id !== 'all' || filters.person_type !== 'all' || filters.status !== 'all') && (
          <button
            onClick={() => setFilters({ ...filters, campus_id: 'all', person_type: 'all', status: 'all' })}
            className="text-xs text-blue-600 hover:underline ml-auto"
          >
            Clear extra filters
          </button>
        )}
      </div>

      {/* ─── Bulk actions bar (only for writers) ─── */}
      {canWrite && selectedCount > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded mb-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-blue-800">
            <i className="fas fa-check-square mr-1"></i>
            <strong>{selectedCount}</strong> record(s) selected · Total: <strong>{CURRENCY}{selectedTotal.toLocaleString()}</strong>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-1 text-sm bg-white text-gray-700 rounded border border-gray-300"
            >
              Clear
            </button>
            <button
              onClick={handleBulkMarkPaid}
              className="px-4 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
            >
              <i className="fas fa-check-double mr-1"></i> Mark All Paid
            </button>
          </div>
        </div>
      )}

      {loading ? <p>Loading...</p> : (
        <div className="bg-white p-4 rounded shadow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(col => (
                    <th key={col} className="p-3 text-left font-semibold text-gray-700">
                      {col === '' ? (
                        <input
                          type="checkbox"
                          className="w-4 h-4 cursor-pointer"
                          checked={allSelected}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedIds(new Set(selectableIds));
                            else setSelectedIds(new Set());
                          }}
                        />
                      ) : col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="p-6 text-center text-gray-400">
                      No salary records for this filter.
                    </td>
                  </tr>
                ) : data.map((row, i) => (
                  <tr
                    key={i}
                    className={`border-t ${selectedIds.has(row._salaryId) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    {columns.map(col => (
                      <td key={col} className="p-3 text-gray-800">{row[col] || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Add Salary Modal (writers only) ─── */}
      <Modal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title="Add Salary Record"
        onSave={handleAddSalary}
        saveLabel="Save"
        size="lg"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Staff Category</label>
            <select
              className="w-full p-2 border rounded"
              value={form.person_type}
              onChange={(e) => setForm({ ...form, person_type: e.target.value, person_id: '' })}
            >
              {PERSON_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Person</label>
            <select
              className="w-full p-2 border rounded"
              value={form.person_id}
              onChange={(e) => setForm({ ...form, person_id: e.target.value })}
            >
              <option value="">— Select —</option>
              {personOptions.map(p => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name}
                  {form.person_type === 'admin' && p.role ? ` (${p.role})` : ''}
                  {form.person_type === 'staff' && p.designation ? ` (${p.designation})` : ''}
                </option>
              ))}
            </select>
          </div>

          <input
            className="w-full p-2 border rounded"
            type="number"
            placeholder={`Basic Salary (${CURRENCY.trim()})`}
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />

          <div className="grid grid-cols-2 gap-3">
            <select
              className="w-full p-2 border rounded"
              value={form.month}
              onChange={(e) => setForm({ ...form, month: parseInt(e.target.value) })}
            >
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <input
              className="w-full p-2 border rounded"
              type="number"
              placeholder="Year"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) })}
            />
          </div>

          <input
            className="w-full p-2 border rounded"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </Modal>

      {/* ─── Mark Paid Modal ─── */}
      <Modal
        isOpen={!!payModal}
        onClose={() => setPayModal(null)}
        title={`Mark Salary as Paid – ${payModal?.person_name || ''}`}
        onSave={() => handleMarkPaid(payModal.id)}
        saveLabel="Mark Paid & Download Payslip"
      >
        <div className="space-y-2">
          <p><strong>Amount:</strong> {CURRENCY}{payModal?.amount?.toLocaleString()}</p>
          <p><strong>Period:</strong> {MONTHS[(payModal?.month || 1) - 1]} {payModal?.year}</p>
          <p className="text-sm text-gray-500">This salary will be marked <strong>Paid</strong> with today's date.</p>
        </div>
      </Modal>

      {/* ─── Edit Salary Modal ─── */}
      <Modal
        isOpen={!!editModal}
        onClose={() => setEditModal(null)}
        title={`Edit Salary – ${editModal?.person_name || ''}`}
        onSave={handleSaveEdit}
        saveLabel="Save Changes"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded text-sm">
            <p><strong>Type:</strong> {editModal?.person_type}</p>
            <p><strong>Period:</strong> {MONTHS[(editModal?.month || 1) - 1]} {editModal?.year}</p>
            <p><strong>Status:</strong> {editModal?.status}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Basic Salary ({CURRENCY.trim()})</label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              value={editForm.amount}
              onChange={e => setEditForm({ ...editForm, amount: e.target.value })}
              min="0"
              step="0.01"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium">Adjustments ({editForm.adjustments.length})</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => addAdjustment('add')}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded"
                >
                  <i className="fas fa-plus mr-1"></i> Bonus
                </button>
                <button
                  type="button"
                  onClick={() => addAdjustment('deduct')}
                  className="px-3 py-1 text-xs bg-red-600 text-white rounded"
                >
                  <i className="fas fa-minus mr-1"></i> Deduction
                </button>
              </div>
            </div>
            {editForm.adjustments.length === 0 ? (
              <p className="text-xs text-gray-400 italic p-3 bg-gray-50 rounded text-center">No adjustments yet</p>
            ) : (
              <div className="space-y-2">
                {editForm.adjustments.map((adj, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded border ${
                      adj.type === 'add' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      adj.type === 'add' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                    }`}>
                      {adj.type === 'add' ? '+' : '−'}
                    </span>
                    <input
                      className="flex-1 p-1 border rounded text-xs"
                      placeholder="Reason"
                      value={adj.reason}
                      onChange={e => updateAdjustment(idx, 'reason', e.target.value)}
                    />
                    <input
                      type="number"
                      className="w-24 p-1 border rounded text-xs"
                      placeholder="Amount"
                      value={adj.value}
                      onChange={e => updateAdjustment(idx, 'value', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => removeAdjustment(idx)}
                      className="text-red-500 hover:text-red-700 px-1"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-700 font-medium">Net Payable:</span>
              <span className="text-2xl font-bold text-blue-900">{CURRENCY}{editNet.toLocaleString()}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes</label>
            <input
              className="w-full p-2 border rounded"
              placeholder="Optional notes"
              value={editForm.notes}
              onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}